import { View } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import type { RouteProp } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react-native'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import type { AdminAuthStackParamList } from '../../../navigation/types'
import { Button, InlineAlert, TextField } from '@chillout/ui'
import { AdminAuthLayout } from './AdminAuthLayout'
import { AuthScreenHeader } from './AuthScreenHeader'
import { BackToSignInLink } from './BackToSignInLink'
import { getAdminPasswordResetRedirectUrl } from './authUrl'
import { forgotPasswordSchema } from './authSchemas'

export function AdminForgotPasswordScreen() {
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const route = useRoute<RouteProp<AdminAuthStackParamList, 'ForgotPassword'>>()

  const { control, handleSubmit, setError, clearErrors, getValues, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(forgotPasswordSchema(t)),
    defaultValues: { email: route.params?.email ?? '' },
  })

  const onSubmit = handleSubmit(async ({ email }) => {
    const trimmedEmail = email.trim()
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: getAdminPasswordResetRedirectUrl(),
      })

      if (error) {
        reportUnexpectedError(error)
        setError('root', { message: t('adminAuth.resetLinkRequestError') })
        return
      }

      navigation.navigate('CheckEmail', { email: trimmedEmail })
    } catch (err) {
      reportUnexpectedError(err)
      setError('root', { message: t('adminAuth.resetLinkRequestError') })
    }
  })

  return (
    <AdminAuthLayout>
      <AuthScreenHeader
        Icon={Mail}
        iconAccessibilityLabel={t('adminAuth.forgotPasswordIconAccessibilityLabel')}
        title={t('adminAuth.forgotPasswordTitle')}
        subtitle={t('adminAuth.forgotPasswordSubtitle')}
      />
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <TextField
            label={t('adminAuth.emailLabel')}
            placeholder={t('adminAuth.emailPlaceholder')}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            textContentType="emailAddress"
            prefix={Mail}
            value={field.value}
            onChangeText={(v) => { field.onChange(v); clearErrors('root') }}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
            className="mt-8 sm:mt-10"
          />
        )}
      />
      {errors.root ? (
        <View className="mt-6">
          <InlineAlert message={errors.root.message ?? ''} />
        </View>
      ) : null}
      <Button
        label={t('adminAuth.sendResetLinkButton')}
        loadingLabel={t('adminAuth.sendingResetLinkButton')}
        loading={isSubmitting}
        onPress={onSubmit}
        className={errors.root ? 'mt-6' : 'mt-10'}
      />
      <BackToSignInLink
        onPress={() => navigation.navigate('SignIn', { email: getValues('email').trim() })}
        className="mt-6"
      />
    </AdminAuthLayout>
  )
}